import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function TestView() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [, setDebugInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [testInfo, setTestInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'test', or 'answers'
  const [questionTab, setQuestionTab] = useState(0); // For navigating between questions in test/answers mode
  const [userAnswers, setUserAnswers] = useState({}); // Store user's answers for interactive mode
  const [showFeedback, setShowFeedback] = useState({}); // Track which questions show feedback
  const [headerImages, setHeaderImages] = useState([]); // Store header images (up to 2)
  const [votes, setVotes] = useState({
    'VMBO BB': 0,
    'VMBO KB': 0,
    'VMBO GT': 0,
    'HAVO': 0,
    'VWO': 0
  }); // Store vote counts for each level
  const [userVote, setUserVote] = useState(null); // Store which level the user voted for
  const [showVotingModal, setShowVotingModal] = useState(false); // Control voting modal visibility
  const [isVotingLoading, setIsVotingLoading] = useState(false); // Track voting loading state

  // Helper function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Helper function to set cookie
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  // Helper function to get cookie key for this test
  const getVoteCookieKey = () => {
    return `vote_${testId}`;
  };

  useEffect(() => {
    loadTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const loadTest = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/test-status/${testId}`);
      const statusData = response.data;
      
      console.log('Test status data:', statusData);
      console.log('Test info:', statusData.test);
      
      // Update debug info with loaded test data
      updateDebugInfoFromStatus(statusData);
      
      // Load vote data from API
      if (statusData.votes) {
        setVotes(statusData.votes);
      }
      
      // Check cookie for user vote (as fallback/backup)
      const cookieVote = getCookie(getVoteCookieKey());
      if (cookieVote) {
        setUserVote(cookieVote);
      } else if (statusData.user_vote) {
        // Use server-side vote if available
        setUserVote(statusData.user_vote);
        // Save to cookie
        setCookie(getVoteCookieKey(), statusData.user_vote);
      }
      
    } catch (error) {
      console.error('Error loading test:', error);
      setError('Fout bij het laden van de test. Controleer of de test bestaat.');
    } finally {
      setLoading(false);
    }
  };

  // Update debug info from database status
  const updateDebugInfoFromStatus = (statusData) => {
    const newDebugInfo = {};
    const questionsArray = [];
    
    // Add safety check for statusData
    if (!statusData || !statusData.questions) {
      console.warn('Invalid statusData received:', statusData);
      return;
    }
    
    // Add strategy if available
    if (statusData.test?.strategy_data) {
      newDebugInfo.strategy = statusData.test.strategy_data;
    }
    
    // Helper function to safely parse options
    const parseOptions = (options) => {
      if (!options) return ["Option A", "Option B", "Option C", "Option D"];
      if (Array.isArray(options)) {
        // Ensure all items are strings and not empty
        return options.filter(opt => opt && typeof opt === 'string').length > 0 
          ? options.filter(opt => opt && typeof opt === 'string')
          : ["Option A", "Option B", "Option C", "Option D"];
      }
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            return parsed.filter(opt => opt && typeof opt === 'string').length > 0 
              ? parsed.filter(opt => opt && typeof opt === 'string')
              : ["Option A", "Option B", "Option C", "Option D"];
          }
          return ["Option A", "Option B", "Option C", "Option D"];
        } catch (e) {
          console.warn('Failed to parse options JSON:', e);
          return ["Option A", "Option B", "Option C", "Option D"];
        }
      }
      return ["Option A", "Option B", "Option C", "Option D"];
    };
    
    // Add questions data and build questions array
    if (Array.isArray(statusData.questions)) {
      statusData.questions.forEach(question => {
        const questionData = {
          id: question.id,
          question_number: question.question_number,
          question_type: question.question_type,
          question_text: question.question_text,
          options: parseOptions(question.options),
          correct_answer: question.correct_answer,
          explanation: question.explanation,
          question_type_description: question.question_type_description,
          script: question.script,
          generation_metadata: question.generation_metadata,
          images: question.images || [],
          audio_url: question.audio_url,
          audio_file_path: question.audio_file_path,
          final_video_url: question.final_video_url,
          final_video_path: question.final_video_path
        };
        
        questionsArray.push(questionData);
        
        // Keep backward compatibility with debugInfo
        if (question.question_number === 1 && question.question_type === 'audio') {
          newDebugInfo.audioContent = {
            script: question.script,
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            story_type: question.generation_metadata?.story_type,
            emotional_tone: question.generation_metadata?.emotional_tone
          };
          
          if (question.audio_url) {
            newDebugInfo.audio = {
              audio_file: question.audio_file_path,
              audio_url: question.audio_url,
              duration: "30 seconds",
              script: question.script,
              status: "success"
            };
          }
        } else if (question.question_number === 2 && question.question_type === 'video') {
          newDebugInfo.videoContent = {
            script: question.script,
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            visual_elements: question.generation_metadata?.visual_elements,
            presentation_style: question.generation_metadata?.presentation_style,
            visual_theme: question.generation_metadata?.visual_theme,
            image_urls: question.images?.map(img => img.image_url) || [],
            image_paths: question.images?.map(img => img.image_path) || []
          };
          
          if (question.final_video_url) {
            newDebugInfo.final_video = {
              final_video: question.final_video_path,
              video_url: question.final_video_url,
              duration: "30.0 seconds",
              avatar_position: "bottom_right",
              slideshow_images: question.images?.length || 0,
              status: "success",
              message: "Final video created successfully"
            };
          }
        } else if (question.question_number === 3 && question.question_type === 'reading') {
          newDebugInfo.readingContent = {
            text: question.script, // Reading text is stored in script field
            question: question.question_text,
            options: parseOptions(question.options),
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            question_type: question.question_type_description,
            text_type: question.generation_metadata?.text_type,
            reading_level: question.generation_metadata?.reading_level,
            key_points: question.generation_metadata?.key_points,
            image_urls: question.images?.map(img => img.image_url) || [],
            image_paths: question.images?.map(img => img.image_path) || []
          };
        }
      });
    }
    
    // Add HeyGen video data
    if (statusData.heygen_videos && statusData.heygen_videos.length > 0) {
      const heygenVideo = statusData.heygen_videos[0];
      newDebugInfo.avatar_video = {
        video_id: heygenVideo.heygen_video_id,
        status: heygenVideo.status,
        video_url: heygenVideo.video_url,
        thumbnail_url: heygenVideo.thumbnail_url,
        script: heygenVideo.script
      };
    }
    
    // Only update debug info if we have new data, preserve existing data
    if (Object.keys(newDebugInfo).length > 0) {
      setDebugInfo(prev => ({ ...prev, ...newDebugInfo }));
    }
    setQuestions(questionsArray.sort((a, b) => a.question_number - b.question_number));
    
    // Include steps in test info for failed step display
    const testInfoWithSteps = {
      ...statusData.test,
      steps: statusData.steps || []
    };
    setTestInfo(testInfoWithSteps);
    
    console.log('Setting test info:', testInfoWithSteps);
    
    // Extract up to 2 header images from questions
    const headerImagesArray = [];
    
    // First, try to get images from video questions
    const videoQuestion = statusData.questions?.find(q => q.question_type === 'video' && q.images && q.images.length > 0);
    if (videoQuestion && videoQuestion.images[0]) {
      headerImagesArray.push(getMediaUrl(videoQuestion.images[0].image_url));
    }
    
    // Then, try to get image from reading questions if we don't have 2 yet
    if (headerImagesArray.length < 2) {
      const readingQuestion = statusData.questions?.find(q => q.question_type === 'reading' && q.images && q.images.length > 0);
      if (readingQuestion && readingQuestion.images[0]) {
        headerImagesArray.push(getMediaUrl(readingQuestion.images[0].image_url));
      }
    }
    
    // If we still don't have 2, try to get another image from the same video question
    if (headerImagesArray.length < 2 && videoQuestion && videoQuestion.images && videoQuestion.images.length > 1) {
      headerImagesArray.push(getMediaUrl(videoQuestion.images[1].image_url));
    }
    
    setHeaderImages(headerImagesArray);
  };

  // Helper function to get language emoji
  const getLanguageEmoji = (language) => {
    switch (language) {
      case 'nl': return '🇳🇱';
      case 'en': return '🇬🇧';
      case 'fr': return '🇫🇷';
      case 'es': return '🇪🇸';
      default: return '🌐';
    }
  };

  // Helper function to get status text and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { text: 'Voltooid', color: '#4CAF50', emoji: '✅' };
      case 'failed':
        return { text: 'Mislukt', color: '#F44336', emoji: '❌' };
      case 'in_progress':
        return { text: 'Bezig', color: '#FF9800', emoji: '⏳' };
      case 'pending':
        return { text: 'Wachtend', color: '#9E9E9E', emoji: '⏸️' };
      default:
        return { text: 'Onbekend', color: '#9E9E9E', emoji: '❓' };
    }
  };

  // Helper function to check if test is fully available for taking
  const isTestFullyAvailable = () => {
    if (!testInfo || testInfo.status !== 'completed') {
      return false;
    }
    
    // Check if all questions have required media
    return questions.every(question => {
      if (question.question_type === 'audio') {
        return question.audio_url;
      } else if (question.question_type === 'video') {
        return question.final_video_url || (question.images && question.images.length > 0);
      } else if (question.question_type === 'reading') {
        return question.script && (question.images && question.images.length > 0);
      }
      return true;
    });
  };

  // Helper function to handle user answer selection
  const handleAnswerSelect = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    // Show immediate feedback
    setShowFeedback(prev => ({
      ...prev,
      [questionId]: true
    }));
  };

  // Status check function - redirects to generation page
  const checkTestStatus = () => {
    navigate(`/generate/${testId}`, {
      state: {
        topic: testInfo?.topic,
        language: testInfo?.language
      }
    });
  };


  // Helper function to get the correct media URL (handles both local and S3 URLs)
  const getMediaUrl = (url) => {
    if (!url) return '';
    // If it's already a full URL (S3), return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If it's a local path, prepend API_BASE_URL
    return `${API_BASE_URL}${url}`;
  };

  // Helper function to render media content
  const renderMediaContent = (question) => {
    if (question.question_type === 'audio' && question.audio_url) {
      return (
        <div className="media-content">
          <h4 className="question-number">Vraag {question.question_number}</h4>
          <p style={{textAlign: 'left'}}>Beluister onderstaand audio fragment en selecteer het juiste antwoord.</p>
          <div className="audio-container">
            <audio 
              controls 
              className="generated-audio"
              onError={(e) => {
                e.target.style.display = 'none';
                const nextSibling = e.target.nextSibling;
                if (nextSibling && nextSibling.style) {
                  nextSibling.style.display = 'block';
                }
              }}
            >
              <source src={getMediaUrl(question.audio_url)} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
            <div className="audio-error" style={{display: 'none'}}>
              Error loading audio file
            </div>
          </div>
          {question.script && activeTab !== 'test' && (
            <div className="script-section">
              <h5>Script</h5>
              <p>{question.script}</p>
            </div>
          )}
        </div>
      );
    } else if (question.question_type === 'video') {
      return (
        <div className="media-content">
          <h4 className="question-number">Vraag {question.question_number}</h4>
          {question.final_video_url ? (
            <p style={{textAlign: 'left'}}>Bekijk onderstaand video fragment en selecteer het juiste antwoord.</p>
          ):(<p></p>)}
          {question.final_video_url ? (
            <div className="video-container">
              <video 
                controls 
                className="generated-video"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const nextSibling = e.target.nextSibling;
                  if (nextSibling && nextSibling.style) {
                    nextSibling.style.display = 'block';
                  }
                }}
              >
                <source src={getMediaUrl(question.final_video_url)} type="video/mp4" />
                Your browser does not support the video element.
              </video>
              <div className="video-error" style={{display: 'none'}}>
                Error loading video file
              </div>
            </div>
          ) : question.images && question.images.length > 0 ? (
            <div className="image-gallery">
              <h5>Generated Images</h5>
              <div className="images-container">
                {question.images.map((image, index) => (
                  <div key={index} className="image-item">
                    <img 
                      src={getMediaUrl(image.image_url)} 
                      alt={`Generated ${index + 1}`}
                      className="generated-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const nextSibling = e.target.nextSibling;
                        if (nextSibling && nextSibling.style) {
                          nextSibling.style.display = 'block';
                        }
                      }}
                    />
                    <div className="image-error" style={{display: 'none'}}>
                      Error loading image
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-media">
              <p>No media available for this question.</p>
            </div>
          )}
          {question.script && activeTab !== 'test' && (
            <div className="script-section">
              <h5>Script</h5>
              <p>{question.script}</p>
            </div>
          )}
        </div>
      );
    } else if (question.question_type === 'reading') {
      return (
        <div className="media-content">
          <h4 className="question-number">Vraag {question.question_number}</h4>
          <p style={{textAlign: 'left'}}>Lees onderstaande tekst en selecteer het juiste antwoord.</p>
          {/* Reading Image */}
          {question.images && question.images.length > 0 && (
            <div className="reading-image-container">
              <img 
                src={getMediaUrl(question.images[0].image_url)} 
                alt="Reading comprehension"
                className="reading-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const nextSibling = e.target.nextSibling;
                  if (nextSibling && nextSibling.style) {
                    nextSibling.style.display = 'block';
                  }
                }}
              />
              <div className="image-error" style={{display: 'none'}}>
                Error loading image
              </div>
            </div>
          )}
          {/* Reading Text */}
          {question.script && (
            <div className="reading-text-container">
              <div className="reading-text">
                {question.script.split('\n').map((paragraph, index) => (
                  <p key={index} className="reading-paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          
        </div>
      );
    }
    return (
      <div className="media-content">
        <h4 className="question-number">Vraag {question.question_number}</h4>
        <div className="no-media">
          <p>No media available for this question type.</p>
        </div>
      </div>
    );
  };

  // Helper function to handle voting
  const handleVote = async (level) => {
    if (isVotingLoading || !isTestFullyAvailable()) return; // Prevent voting if test is not available or already loading
    
    setIsVotingLoading(true);
    try {
      // If clicking the same level, remove vote
      if (userVote === level) {
        // Remove vote
        await axios.delete(`${API_BASE_URL}/tests/${testId}/vote`);
        setUserVote(null);
        // Update vote counts (recalculate from server)
        const response = await axios.get(`${API_BASE_URL}/test-status/${testId}`);
        if (response.data.votes) {
          setVotes(response.data.votes);
        }
        // Remove cookie
        setCookie(getVoteCookieKey(), '', -1);
      } else {
        // Create or update vote
        const response = await axios.post(`${API_BASE_URL}/tests/${testId}/vote`, {
          niveau: level
        });
        
        if (response.data.vote_counts) {
          setVotes(response.data.vote_counts);
        }
        setUserVote(response.data.user_vote || level);
        // Save to cookie
        setCookie(getVoteCookieKey(), level);
      }
    } catch (error) {
      console.error('Error voting:', error);
      // Still update UI optimistically on error
      if (userVote === level) {
        setUserVote(null);
        // Remove cookie
        setCookie(getVoteCookieKey(), '', -1);
      } else {
        setUserVote(level);
        // Save to cookie
        setCookie(getVoteCookieKey(), level);
      }
    } finally {
      setIsVotingLoading(false);
    }
  };

  // Helper function to render compact sidebar voting UI (read-only)
  const renderVotingSidebar = () => {
    const totalVotes = Object.values(votes).reduce((sum, count) => sum + count, 0);
    const levels = ['VMBO BB', 'VMBO KB', 'VMBO GT', 'HAVO', 'VWO'];
    const isTestAvailable = isTestFullyAvailable();

    return (
      <div className="voting-sidebar">
        <div className="voting-card-compact">
          <h3 className="voting-title-compact">Voor welk niveau is dit geschikt?</h3>
          
          {isTestAvailable && (
            <>
              <div className="voting-stats-compact">
                <span className="voting-icon-small">👥</span>
                <span className="voting-count-text">{totalVotes} stemmen</span>
              </div>
              
              <div className="voting-options-compact">
                {levels.map((level) => {
                  const voteCount = votes[level] || 0;
                  const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                  
                  return (
                    <div key={level} className="voting-option-compact">
                      <span className="voting-option-name-compact">{level}</span>
                      <div className="voting-progress-container-compact">
                        <div
                          className="voting-progress-bar-compact"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="voting-percentage-compact">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
              
              <button 
                className="share-opinion-button"
                onClick={() => setShowVotingModal(true)}
                title="Deel je mening over het niveau"
              >
                Deel je mening
              </button>
            </>
          )}
          
          {!isTestAvailable && (
            <div className="voting-disabled-message">
              <p>⏳ Stemmen is beschikbaar zodra de toets volledig gegenereerd is.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper function to render full voting UI in modal
  const renderVotingModal = () => {
    if (!showVotingModal) return null;
    
    const totalVotes = Object.values(votes).reduce((sum, count) => sum + count, 0);
    const levels = ['VMBO BB', 'VMBO KB', 'VMBO GT', 'HAVO', 'VWO'];
    const isTestAvailable = isTestFullyAvailable();
    const isDisabled = !isTestAvailable || isVotingLoading;

    return (
      <div className="voting-modal-overlay" onClick={() => !isVotingLoading && setShowVotingModal(false)}>
        <div className="voting-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Loading Overlay */}
          {isVotingLoading && (
            <div className="voting-loading-overlay">
              <div className="voting-loading-spinner"></div>
              <p>Stem wordt verwerkt...</p>
            </div>
          )}
          
          <div className="voting-modal-header">
            <h2 className="voting-title">Voor welk niveau is dit lesmateriaal geschikt?</h2>
            <button 
              className="voting-modal-close"
              onClick={() => !isVotingLoading && setShowVotingModal(false)}
              disabled={isVotingLoading}
            >
              ×
            </button>
          </div>
          
          <div className="voting-modal-body" style={{ opacity: isDisabled ? 0.6 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
            {!isTestAvailable && (
              <div className="voting-disabled-notice">
                <p>⏳ Stemmen is beschikbaar zodra de toets volledig gegenereerd is.</p>
              </div>
            )}
            
            <div className="voting-subtitle">
              <span className="voting-icon">👥</span>
              <span>{totalVotes} docenten hebben gestemd</span>
            </div>
            
            <div className="voting-options">
              {levels.map((level) => {
                const voteCount = votes[level] || 0;
                const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                const isSelected = userVote === level;
                
                return (
                  <div
                    key={level}
                    className={`voting-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && handleVote(level)}
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="voting-option-content">
                      <span className="voting-option-name">{level}</span>
                      <div className="voting-progress-container">
                        <div
                          className="voting-progress-bar"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="voting-thumbs-up">👍</span>
                      <span className="voting-count">{voteCount}</span>
                      <span className="voting-percentage">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="voting-footer">
              <p>{isTestAvailable ? 'Klik op een niveau om je stem uit te brengen' : 'Wacht tot de toets volledig gegenereerd is'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get language name
  const getLanguageName = (code) => {
    const languages = {
      'nl': 'Nederlands',
      'en': 'English',
      'fr': 'Français',
      'es': 'Español'
    };
    return languages[code] || code;
  };

  // Helper function to render test info tab
  const renderTestInfo = () => {
    const audioQuestions = questions.filter(q => q.question_type === 'audio').length;
    const videoQuestions = questions.filter(q => q.question_type === 'video').length;
    const readingQuestions = questions.filter(q => q.question_type === 'reading').length;

    return (
      <div className="test-info-tab">
        {testInfo && (
          <div className="test-info-content">
            {headerImages.length > 0 && (
              <div className="test-info-images">
                {headerImages.map((imageUrl, index) => (
                  <div 
                    key={index}
                    className="test-info-image"
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  />
                ))}
              </div>
            )}
            <div className="test-info-details">
              <h2 className="test-topic">{testInfo.topic}</h2>
              
              <div className="test-info-grid">
                <div className="test-info-item">
                  <span className="info-label">Taal:</span>
                  <span className="info-value">
                    <span className="language-flag">{getLanguageEmoji(testInfo.language)}</span>
                    {getLanguageName(testInfo.language)}
                  </span>
                </div>
                
                <div className="test-info-item">
                  <span className="info-label">Status:</span>
                  <span className="info-value" style={{ 
                    color: getStatusInfo(testInfo.status).color 
                  }}>
                    {getStatusInfo(testInfo.status).emoji} {getStatusInfo(testInfo.status).text}
                  </span>
                </div>
                
                {testInfo.niveau && testInfo.niveau !== 'NOT_SET' && (
                  <div className="test-info-item">
                    <span className="info-label">Niveau:</span>
                    <span className="info-value">{testInfo.niveau}</span>
                  </div>
                )}
                
                <div className="test-info-item">
                  <span className="info-label">Aantal vragen:</span>
                  <span className="info-value">{questions.length}</span>
                </div>
                
                {questions.length > 0 && (
                  <div className="test-info-item">
                    <span className="info-label">Vraagtypen:</span>
                    <span className="info-value">
                      {audioQuestions > 0 && `${audioQuestions} audio`}
                      {audioQuestions > 0 && (videoQuestions > 0 || readingQuestions > 0) && ', '}
                      {videoQuestions > 0 && `${videoQuestions} video`}
                      {videoQuestions > 0 && readingQuestions > 0 && ', '}
                      {readingQuestions > 0 && `${readingQuestions} leesvaardigheid`}
                    </span>
                  </div>
                )}
                
                {testInfo.created_at && (
                  <div className="test-info-item">
                    <span className="info-label">Aangemaakt:</span>
                    <span className="info-value">{formatDate(testInfo.created_at)}</span>
                  </div>
                )}
                
                {testInfo.completed_at && (
                  <div className="test-info-item">
                    <span className="info-label">Voltooid:</span>
                    <span className="info-value">{formatDate(testInfo.completed_at)}</span>
                  </div>
                )}
              </div>
              
              <div className="test-info-actions">
                <button 
                  onClick={() => setActiveTab('test')}
                  className={`test-action-button test-button ${!isTestFullyAvailable() ? 'disabled' : ''}`}
                  disabled={!isTestFullyAvailable()}
                  title={!isTestFullyAvailable() ? 'Test is nog niet volledig beschikbaar' : 'Start de interactieve test'}
                >
                  📝 Doe de test
                </button>
                <button 
                  onClick={() => setActiveTab('answers')}
                  className={`test-action-button answers-button ${isTestInProgress ? 'disabled' : ''}`}
                  disabled={isTestInProgress}
                  title={isTestInProgress ? 'Test is nog niet klaar' : 'Bekijk alle antwoorden en scripts'}
                >
                  👁️ Bekijk antwoorden
                </button>
              </div>
              
              {!isTestFullyAvailable() && testInfo.status === 'completed' && (
                <div className="availability-notice">
                  <p>⚠️ Deze test is nog niet volledig beschikbaar. Alle media moet gegenereerd zijn voordat je de test kunt maken.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render question content
  const renderQuestionContent = (question) => {
    const isInteractiveMode = activeTab === 'test';
    const userAnswer = userAnswers[question.id];
    const hasFeedback = showFeedback[question.id];
    const isCorrect = userAnswer === question.correct_answer;
    
    return (
      <div className="question-content">
        <div className="question-text">
          <p>{question.question_text}</p>
        </div>
        
        <div className="question-options">
          {question.options && Array.isArray(question.options) && question.options.map((option, index) => {
            const optionLetter = String.fromCharCode(65 + index);
            const isSelected = userAnswer === optionLetter;
            const isCorrectOption = question.correct_answer === optionLetter;
            
            // Determine styling based on mode and feedback
            let optionClass = 'option';
            if (isInteractiveMode) {
              optionClass += ' interactive';
              if (isSelected) {
                optionClass += ' selected';
              }
              if (hasFeedback && isCorrectOption) {
                optionClass += ' correct';
              }
              if (hasFeedback && isSelected && !isCorrectOption) {
                optionClass += ' incorrect';
              }
            } else {
              if (isCorrectOption) {
                optionClass += ' correct';
              }
            }
            
            return (
              <div 
                key={index} 
                className={optionClass}
                onClick={isInteractiveMode && !hasFeedback ? () => handleAnswerSelect(question.id, optionLetter) : undefined}
                style={{ cursor: isInteractiveMode && !hasFeedback ? 'pointer' : 'default' }}
              >
                <span className="option-label">{optionLetter}.</span>
                <span className="option-text">{option}</span>
                {!isInteractiveMode && isCorrectOption && (
                  <span className="correct-indicator">✓</span>
                )}
                {isInteractiveMode && hasFeedback && isCorrectOption && (
                  <span className="correct-indicator">✓</span>
                )}
                {isInteractiveMode && hasFeedback && isSelected && !isCorrectOption && (
                  <span className="incorrect-indicator">✗</span>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Show feedback in interactive mode */}
        {isInteractiveMode && hasFeedback && (
          <div className={`feedback-section ${isCorrect ? 'correct-feedback' : 'incorrect-feedback'}`}>
            {isCorrect ? (
              <div className="feedback-message correct">
                <span className="feedback-icon">🎉</span>
                <span className="feedback-text">Correct! Goed gedaan!</span>
              </div>
            ) : (
              <div className="feedback-message incorrect">
                <span className="feedback-icon">❌</span>
                <span className="feedback-text">Helaas, dat is niet correct.</span>
              
              </div>
            )}
            {question.explanation && (
              <div className="feedback-explanation">
                <strong>Uitleg:</strong> {question.explanation}
              </div>
            )}
          </div>
        )}
        
        {/* Show correct answer and explanation only in answers mode */}
        {!isInteractiveMode && question.correct_answer && (
          <div className="correct-answer">
            <p><strong>Correct antwoord:</strong> {question.correct_answer}</p>
          </div>
        )}
        
        {!isInteractiveMode && question.explanation && (
          <div className="explanation">
            <p><strong>Uitleg:</strong> {question.explanation}</p>
          </div>
        )}
        
        {!isInteractiveMode && question.question_type_description && (
          <div className="question-type">
            <p><strong>Type:</strong> {question.question_type_description}</p>
          </div>
        )}
        
        {!isInteractiveMode && question.generation_metadata && (
          <div className="question-metadata">
            {question.generation_metadata.story_type && (
              <p><strong>Verhaal type:</strong> {question.generation_metadata.story_type}</p>
            )}
            {question.generation_metadata.emotional_tone && (
              <p><strong>Emotionele toon:</strong> {question.generation_metadata.emotional_tone}</p>
            )}
            {question.generation_metadata.visual_theme && (
              <p><strong>Visueel thema:</strong> {question.generation_metadata.visual_theme}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="App">
        <main className="App-main">
          <div className="loading-tests">
            <div className="loading-spinner">⏳</div>
            <p>Test wordt geladen...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <main className="App-main">
          <div className="error-section">
            <h3>❌ {error}</h3>
            <button 
              onClick={() => navigate('/')}
              className="back-button"
            >
              ← Terug naar Homepage
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Check if test is in progress
  const isTestInProgress = testInfo && testInfo.status !== 'completed';

  // Get first image for background
  const backgroundImage = headerImages.length > 0 ? headerImages[0] : null;

  return (
    <div 
      className="App"
      style={backgroundImage ? {
        '--background-image': `url(${backgroundImage})`
      } : {}}
    >
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <div className="breadcrumbs-container">
          <button onClick={() => navigate('/')} className="breadcrumb-link">
            Home
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {testInfo ? testInfo.topic : `Test ${testId}`}
          </span>
        </div>
      </div>
      
      {/* In Progress Banner */}
      {isTestInProgress && (
        <div className="in-progress-banner">
          <div className="in-progress-banner-content">
            <span className="in-progress-icon">⏳</span>
            <span className="in-progress-text">
              Deze toets wordt nog gegenereerd en is daarom nog niet volledig beschikbaar.
            </span>
            <button 
              onClick={checkTestStatus}
              className="in-progress-link"
            >
              Bekijk status
            </button>
          </div>
        </div>
      )}
      
      <main className={`App-main two-column-layout ${!loading && !error ? 'fade-in' : ''}`}>
        {/* Left Column - 80% */}
        <div className="left-column">
          <div className="main-tabs">
            <div className="main-tab-navigation">
              <button
                className={`main-tab-button ${activeTab === 'info' ? 'active' : ''}`}
                onClick={() => setActiveTab('info')}
              >
                Toets informatie
              </button>
              <button
                className={`main-tab-button ${activeTab === 'test' ? 'active' : ''}`}
                onClick={() => setActiveTab('test')}
                disabled={!isTestFullyAvailable()}
              >
                Doe de test
              </button>
              <button
                className={`main-tab-button ${activeTab === 'answers' ? 'active' : ''}`}
                onClick={() => setActiveTab('answers')}
                disabled={isTestInProgress}
              >
                Bekijk antwoorden
              </button>
            </div>

            <div className="main-tab-content">
              {activeTab === 'info' && renderTestInfo()}
              {activeTab === 'test' && questions.length > 0 && (
                <div className="test-view-container">
                  {/* Question Tab Navigation */}
                  <div className="tab-navigation">
                    {questions.map((question, index) => (
                      <button
                        key={question.id}
                        className={`tab-button ${questionTab === index ? 'active' : ''}`}
                        onClick={() => setQuestionTab(index)}
                      >
                        Vraag {question.question_number} - {question.question_type === 'audio' ? 'Audio' : question.question_type === 'video' ? 'Video' : question.question_type === 'reading' ? 'Reading' : 'Question'}
                      </button>
                    ))}
                  </div>

                  {/* Question Tab Content */}
                  <div className="tab-content">
                    {questions[questionTab] && (
                      <div className="split-view">
                        <div className="media-panel">
                          {renderMediaContent(questions[questionTab])}
                        </div>
                        <div className="question-panel">
                          {renderQuestionContent(questions[questionTab])}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeTab === 'answers' && questions.length > 0 && (
                <div className="test-view-container">
                  {/* Question Tab Navigation */}
                  <div className="tab-navigation">
                    {questions.map((question, index) => (
                      <button
                        key={question.id}
                        className={`tab-button ${questionTab === index ? 'active' : ''}`}
                        onClick={() => setQuestionTab(index)}
                      >
                        Vraag {question.question_number} - {question.question_type === 'audio' ? 'Audio' : question.question_type === 'video' ? 'Video' : question.question_type === 'reading' ? 'Reading' : 'Question'}
                      </button>
                    ))}
                  </div>

                  {/* Question Tab Content */}
                  <div className="tab-content">
                    {questions[questionTab] && (
                      <div className="split-view">
                        <div className="media-panel">
                          {renderMediaContent(questions[questionTab])}
                        </div>
                        <div className="question-panel">
                          {renderQuestionContent(questions[questionTab])}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeTab !== 'info' && questions.length === 0 && (
                <div className="no-questions">
                  <h3>Geen vragen gevonden</h3>
                  <p>Deze test bevat geen vragen of er is een fout opgetreden bij het laden.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - 20% - Voting UI */}
        <div className="right-column">
          {renderVotingSidebar()}
        </div>
      </main>
      
      {/* Voting Modal */}
      {renderVotingModal()}
    </div>
  );
}

export default TestView;
